import React from 'react';

interface InputProps {
  id?: string;
  label?: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'date' | 'time' | 'datetime-local';
  placeholder?: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  icon?: string;
  iconPosition?: 'left' | 'right';
  className?: string;
  fullWidth?: boolean;
  maxLength?: number;
  minLength?: number;
  min?: string;
  max?: string;
  step?: string;
}

// Strips everything except digits and a single decimal point. Native
// type="number" inputs let the browser display raw invalid text (letters,
// repeated +/-/e) that a sanitizing onChange can't visually clear, because
// Chromium/WebKit don't redraw the field once it's in an "invalid number"
// state — the garbage stays on screen even though .value would read empty.
// Rendering as type="text" instead (below) has no such quirk, so this
// sanitizer's result always matches what's actually on screen.
const sanitizeNumeric = (raw: string): string => {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
};

export default function Input({
  id,
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
  required = false,
  disabled = false,
  error,
  helperText,
  icon,
  iconPosition = 'left',
  className = '',
  fullWidth = true,
  maxLength,
  minLength,
  min,
  max,
  step,
}: InputProps) {
  const widthClass = fullWidth ? 'w-full' : '';
  const errorClass = error ? 'border-red-500 focus:ring-red-500' : 'border-ink-200 focus:ring-brand-500';
  const disabledClass = disabled ? 'bg-ink-50 cursor-not-allowed' : 'bg-white';
  const isNumeric = type === 'number';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isNumeric) {
      const sanitized = sanitizeNumeric(e.target.value);
      if (sanitized !== e.target.value) e.target.value = sanitized;
    }
    onChange(e);
  };

  return (
    <div className={`${widthClass} ${className}`}>
      {label && (
        <label className="block text-xs font-bold uppercase tracking-[0.06em] text-ink-600 mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        {icon && iconPosition === 'left' && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <i className={`fas ${icon} text-ink-400 text-sm`}></i>
          </div>
        )}
        <input
          id={id}
          type={isNumeric ? 'text' : type}
          inputMode={isNumeric ? 'decimal' : undefined}
          pattern={isNumeric ? '[0-9]*\\.?[0-9]*' : undefined}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          required={required}
          disabled={disabled}
          maxLength={maxLength}
          minLength={minLength}
          min={isNumeric ? undefined : min}
          max={isNumeric ? undefined : max}
          step={isNumeric ? undefined : step}
          className={`
            ${widthClass}
            ${icon && iconPosition === 'left' ? 'pl-10' : ''}
            ${icon && iconPosition === 'right' ? 'pr-10' : ''}
            px-3 py-2 border rounded-md min-h-[44px] lg:min-h-0 text-sm
            focus:outline-none focus:ring-2 focus:border-transparent
            transition-colors duration-150 text-ink-900
            ${errorClass}
            ${disabledClass}
          `}
        />
        {icon && iconPosition === 'right' && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <i className={`fas ${icon} text-ink-400 text-sm`}></i>
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      {!error && helperText && (
        <p className="mt-1 text-xs text-ink-500">{helperText}</p>
      )}
    </div>
  );
}
